import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import usePath from '../../../components/Hooks/usePath';
import LangEnum from '../../../lang/LangEnum';
import { ClarityEvents } from '../../../lib/clarity/types/clarityEvents.types';
import { captureEvent } from '../../../lib/clarity/utils/captureEvent.utils';
import PATHS from '../../../routes/routes.const';
import { getOrderQueryKey } from '../../../services/modules/order/order.keys';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import snackbarStore from '../../../stores/Snackbar.store';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../translation/TranslationContext';
import { ACTION_TO_ORDER_STATUS_MAP } from '../actions/orderSummaryActions';
import { ActionEnum } from '../actions/types/orderActions.types';
import { HandleOkResponseProps } from '../actions/types/orderActionsContext.types';
import { useOrderCalculateContext } from '../contexts/OrderCalculate.context';
import {
  isActionToChangeToOrderEditMode,
  isActionToFinishOrApproveOrder,
  isActionToRemainInOrderFinishedPage,
} from '../contexts/order.utils';
import { StatusType } from '../order.types';
import { OrderView } from '../ordersView.types';
import { updateOrderStatusRepository } from '../repositories/updateOrderStatus.repository';
import useHandleErrorOnChangingOrderStatus from './useHandleErrorOnChangingOrderStatus';

export interface OnChangeStatus {
  reason?: string;
  action?: ActionEnum;
  orderId?: string;
  brandId?: string;
}

interface UseChangeOrderStatus {
  onSuccess?: (newStatus?: StatusType) => void;
  ignoreDefaultSuccessHandling?: boolean;
}

const { handleMessage } = snackbarStore;

const useChangeOrderStatus = (props?: UseChangeOrderStatus) => {
  const { translate } = useTranslation();
  const history = useHistory();
  const { isPath } = usePath();
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();
  const location = useLocation<{ filters: any }>();
  const { calculateSummary } = useOrderCalculateContext();
  const { handleError } = useHandleErrorOnChangingOrderStatus();

  const [isSubmittingAction, setIsSubmittingAction] = useState(false);

  const [handleFinishSelectedOrder, handleRemoveOrder] = useOrderBookStore(
    useShallow((state) => [state.handleFinishSelectedOrder, state.handleRemoveOrder]),
  );

  const isOrdersPage = isPath(PATHS.main.orders.path);
  const isOrderEditPage = isPath(PATHS.main.orders.editForm.path);
  const isOrderFinishedPage = isPath(PATHS.main.orders.finished.path);
  const isOrderEditOrFinishedPage = isOrderEditPage || isOrderFinishedPage;

  const refetchOrderRelations = useCallback(
    async (orderId: string) => {
      await queryClient.refetchQueries({
        queryKey: [team?.id, ...getOrderQueryKey({ orderId })],
        type: 'active',
        exact: true,
      });
      calculateSummary();
    },
    [calculateSummary, queryClient, team?.id],
  );

  // TODO: necessário refatoração - evitar tantos if's e separar melhor as responsabilidades
  const handleOkResponse = useCallback(
    async ({ action, orderId }: HandleOkResponseProps) => {
      const desiredStatus = ACTION_TO_ORDER_STATUS_MAP[action];

      if (props?.onSuccess) {
        props?.onSuccess(desiredStatus);
      }

      if (props?.ignoreDefaultSuccessHandling) {
        return;
      }

      const isActionToFinish = isActionToFinishOrApproveOrder(action);

      const mustGoBackToOrderListPage = isActionToFinish && !isOrdersPage;

      if (isActionToFinish) {
        captureEvent(ClarityEvents.ORDER_SCREEN_ORDER_FINISHED);
        handleFinishSelectedOrder(team?.id);
      }

      if (mustGoBackToOrderListPage) {
        history.replace({ pathname: '/orders', search: location.state?.filters });
        return;
      }

      const mustRemoveOrder = action === ActionEnum.MOVE_TO_CANCELED || isActionToFinish;
      if (mustRemoveOrder) {
        handleRemoveOrder(orderId, team?.id);
      }

      const mustGoBackToOrderEditPage =
        isOrderEditOrFinishedPage && isActionToChangeToOrderEditMode(action);
      if (mustGoBackToOrderEditPage) {
        await refetchOrderRelations(orderId);
        history.replace(`/orders/${orderId}/${OrderView.EDIT}`);
        return;
      }

      const mustGoToOrderFinishedPage =
        isOrderEditOrFinishedPage && action === ActionEnum.MOVE_TO_CANCELED;
      if (mustGoToOrderFinishedPage) {
        await refetchOrderRelations(orderId);
        history.replace(`/orders/${orderId}/${OrderView.FINISHED}`);
        return;
      }

      const mustRemainInOrderFinishedPage =
        isOrderFinishedPage && isActionToRemainInOrderFinishedPage(action);
      if (mustRemainInOrderFinishedPage) {
        await refetchOrderRelations(orderId);
        return;
      }

      const mustGoToOrderSuggestedPage = isOrderEditPage && action === ActionEnum.ARCHIVE_ORDER;
      if (mustGoToOrderSuggestedPage) {
        handleRemoveOrder(orderId, team?.id);
        await refetchOrderRelations(orderId);
        history.replace(`/orders/${orderId}/${OrderView.SUGGESTION}`);
        return;
      }

      if (
        [
          ActionEnum.ARCHIVE_ORDER,
          ActionEnum.UNARCHIVE_ORDER,
          ActionEnum.MOVE_TO_REJECTED,
        ].includes(action)
      ) {
        await refetchOrderRelations(orderId);
        return;
      }
    },
    [
      team,
      props,
      history,
      isOrdersPage,
      isOrderEditPage,
      handleRemoveOrder,
      isOrderFinishedPage,
      refetchOrderRelations,
      location.state?.filters,
      isOrderEditOrFinishedPage,
      handleFinishSelectedOrder,
    ],
  );

  const onChangeStatus = async ({
    action,
    brandId,
    orderId,
    reason,
  }: OnChangeStatus): Promise<void> => {
    if (isSubmittingAction) {
      handleMessage({ type: 'info', message: translate(LangEnum.GLOBAL_ACTION_IN_PROGRESS) });
      return;
    }

    setIsSubmittingAction(true);
    const response = await updateOrderStatusRepository({
      reason,
      action: action as ActionEnum,
      orderId: orderId as string,
      brandId,
    });
    setIsSubmittingAction(false);

    if (response?.ok) {
      handleOkResponse({
        action: action as ActionEnum,
        orderId: orderId as string,
      });

      return;
    }

    handleError(response);
    return;
  };

  return {
    onChangeStatus,
    isSubmittingAction,
  };
};

export default useChangeOrderStatus;
